/**
 * quizPrompt.ts
 * Builds the structured system + user prompt for the AI Quiz Generator.
 */

import type { BloomsLevel } from '@types-local/quiz.types';

export interface QuizPromptParams {
  slideTitle: string | null;
  slideBody: string;
  targetBlooms?: BloomsLevel | null;
  questionCount?: number;
}

/**
 * Builds the system instruction for the Gemini API.
 * This is sent as the "system" role message.
 */
export function buildSystemPrompt(): string {
  return `You are an expert educational quiz designer with deep knowledge of Bloom's Taxonomy. 
Your task is to analyze slide content provided by a teacher and generate quiz questions.

You MUST respond ONLY with a valid JSON object — no markdown fences, no explanation, no commentary.
The JSON schema is strictly:
{
  "question": string,
  "options": [string, string, string, string],
  "correctIndex": 0 | 1 | 2 | 3,
  "blooms": "Remember" | "Understand" | "Apply" | "Analyze" | "Evaluate" | "Create",
  "explanation": string
}

Rules:
- "question" must be a clear, unambiguous question directly related to the slide content.
- "options" must contain exactly 4 answer choices. Only one is correct.
- "correctIndex" is the 0-based index of the correct option in the "options" array.
- "blooms" must reflect the cognitive level required to answer the question.
- "explanation" must be 1-2 sentences explaining why the correct answer is right.
- Do NOT include "All of the above" or "None of the above" as options.
- Keep option text concise (under 80 characters each).`;
}

/**
 * Builds the user prompt with the slide content and optional constraints.
 */
export function buildUserPrompt(params: QuizPromptParams): string {
  const { slideTitle, slideBody, targetBlooms, questionCount = 1 } = params;

  let prompt = 'Generate ';
  prompt += questionCount === 1 ? 'one quiz question' : `${questionCount} quiz questions`;
  prompt += ' based on the following slide content:\n\n';

  if (slideTitle) {
    prompt += `SLIDE TITLE: ${slideTitle}\n`;
  }
  if (slideBody) {
    prompt += `SLIDE CONTENT:\n${slideBody}\n`;
  }

  if (targetBlooms) {
    prompt += `\nTarget Bloom's Taxonomy level: ${targetBlooms}`;
    prompt += ` (${getBloomsInstruction(targetBlooms)})`;
  }

  prompt += '\n\nRespond with ONLY the JSON object.';
  return prompt;
}

function getBloomsInstruction(level: BloomsLevel): string {
  const instructions: Record<BloomsLevel, string> = {
    Remember:   'ask students to recall specific facts or definitions',
    Understand: 'ask students to explain a concept in their own words',
    Apply:      'present a scenario where students apply the concept',
    Analyze:    'ask students to compare, differentiate, or deconstruct an idea',
    Evaluate:   'ask students to judge, argue, or defend a position',
    Create:     'ask students to design, propose, or construct something new',
  };
  return instructions[level];
}
